import { useEffect } from "react";
import { playTitleMenuMusic, TITLE_MUSIC_URL } from "../lib/title-music";
import { TITLE_MENU_CLICK_SOUND_URL, TITLE_MENU_HOVER_SOUND_URL } from "../lib/title-ui-audio";
import { TITLE_BACKGROUND_URL } from "./TitleScreenWebGL";
import { mediaUrl } from "../lib/media-url";
import { CookieConsent, openCookiePreferences } from "./CookieConsent";
import styles from "./HomePage.module.css";

type HomePageProps = {
  onOpenDm: () => void;
  onOpenMapCreator: () => void;
  onOpenPlayerRemote: () => void;
};

export function HomePage({ onOpenDm, onOpenMapCreator, onOpenPlayerRemote }: HomePageProps) {
  useEffect(() => {
    const preload = document.createElement("link");
    preload.rel = "preload"; preload.as = "audio"; preload.href = TITLE_MUSIC_URL; preload.type = "audio/mpeg";
    document.head.append(preload);
    const backgroundPreload = document.createElement("link");
    backgroundPreload.rel = "preload"; backgroundPreload.as = "image"; backgroundPreload.href = TITLE_BACKGROUND_URL; backgroundPreload.type = "image/webp";
    document.head.append(backgroundPreload);
    const uiSoundPreloads = [TITLE_MENU_HOVER_SOUND_URL, TITLE_MENU_CLICK_SOUND_URL].map((href) => {
      const soundPreload = document.createElement("link");
      soundPreload.rel = "preload"; soundPreload.as = "audio"; soundPreload.href = href; soundPreload.type = "audio/mpeg";
      document.head.append(soundPreload);
      return soundPreload;
    });
    return () => { preload.remove(); backgroundPreload.remove(); uiSoundPreloads.forEach((soundPreload) => soundPreload.remove()); };
  }, []);
  const openTbltop = () => { void playTitleMenuMusic(); onOpenDm(); };
  return <main className={styles.page}>
    <header className={styles.siteHeader}>
      <div className={styles.headerInner}>
        <a className={styles.brand} href="/" aria-label="TBLTOP home">
          <span className={styles.brandMark}>TT</span>
          <span>
            <strong>TBLTOP</strong>
            <small>Tabletop Director</small>
          </span>
        </a>
        <nav className={styles.nav} aria-label="Homepage navigation">
          <a href="#games">What it does</a>
          <a href="#setup">Your setup</a>
        </nav>
        <button type="button" className={styles.headerAction} onClick={openTbltop}>Open TBLTOP</button>
      </div>
    </header>

    <section className={styles.hero} aria-labelledby="home-title">
      <div className={styles.heroCopy}>
        <p className={styles.eyebrow}>Tabletop games, without the missing-parts problem</p>
        <h1 id="home-title">Play with the pieces you have.</h1>
        <p className={styles.heroText}>TBLTOP turns a projector, TV, or screen laid flat on a table into the shared part of the game. Keep the cards, miniatures, dice, and people. Put everything else on the screen.</p>
        <div className={styles.actions}>
          <button type="button" className={styles.primaryAction} onClick={openTbltop}>Open the table</button>
          <button type="button" className={styles.textAction} onClick={onOpenMapCreator}>Create a map <span aria-hidden="true">→</span></button>
        </div>
      </div>

      <figure className={styles.heroShot}>
        <img crossOrigin="anonymous" src={mediaUrl("/assets/home/tbltop-poker-table.jpg")} alt="TBLTOP poker room showing player seats, cards, and the shared table display" />
        <figcaption>
          <span>Poker room</span>
          <span>Real cards. Virtual table and chips.</span>
        </figcaption>
      </figure>
    </section>

    <section className={styles.positioning} id="games" aria-labelledby="positioning-title">
      <div className={styles.positioningLead}>
        <p className={styles.sectionLabel}>Keep game night physical</p>
        <h2 id="positioning-title">TBLTOP does not replace the table.<br />It replaces what is missing from it.</h2>
      </div>
      <div className={styles.pieceList}>
        <article>
          <p>You bring</p>
          <h3>Cards</h3>
          <span>TBLTOP brings the felt, chip stacks, pot, seats, and game state.</span>
        </article>
        <article>
          <p>You bring</p>
          <h3>Miniatures</h3>
          <span>TBLTOP puts a map, grid, fog, light, weather, and effects underneath them.</span>
        </article>
        <article>
          <p>Players bring</p>
          <h3>Their phones</h3>
          <span>TBLTOP gives each person a private hand, character sheet, and controls.</span>
        </article>
      </div>
    </section>

    <section className={styles.gameExample} aria-labelledby="poker-title">
      <div className={styles.exampleNumber} aria-hidden="true">Poker</div>
      <div className={styles.exampleCopy}>
        <h2 id="poker-title">Have the cards but not the chips?</h2>
        <p>Deal the cards you already own. TBLTOP handles seats, stacks, bets, the pot, and the shared table while each player keeps their hand private.</p>
        <button type="button" className={styles.inlineAction} onClick={openTbltop}>Start a poker table <span aria-hidden="true">→</span></button>
      </div>
      <p className={styles.exampleAnswer}>Play anyway.</p>
    </section>

    <section className={styles.mapSection} aria-labelledby="map-title">
      <figure className={styles.mapShot}>
        <img crossOrigin="anonymous" src={mediaUrl("/assets/home/tbltop-cavern-map.jpg")} alt="A projector-ready cavern encounter map created in TBLTOP" />
      </figure>
      <div className={styles.mapCopy}>
        <p className={styles.sectionLabel}>Map Creator</p>
        <h2 id="map-title">Have the miniatures but not the map?</h2>
        <p>Start with a generated dungeon, cavern, island, region, or world map. Edit the rooms, add props, set the mood, and put the finished map under the miniatures on your table.</p>
        <button type="button" className={styles.inlineAction} onClick={onOpenMapCreator}>Open Map Creator <span aria-hidden="true">→</span></button>
      </div>
    </section>

    <section className={styles.remoteSection} aria-labelledby="remote-title">
      <div className={styles.remoteCopy}>
        <p className={styles.sectionLabel}>Player phones</p>
        <h2 id="remote-title">Private in your hand.<br />Shared when you play it.</h2>
        <p>Friends join from the phones already in their pockets. They can draw a card, check a character sheet, manage their pieces, and send a move to the table without passing one device around.</p>
        <button type="button" className={styles.inlineAction} onClick={onOpenPlayerRemote}>Open a player remote <span aria-hidden="true">→</span></button>
      </div>
      <div className={styles.remoteDetails} aria-label="Player remote capabilities">
        <div><span>Private</span><strong>Cards and character details stay with the player.</strong></div>
        <div><span>Connected</span><strong>Moves go to the same local table session.</strong></div>
        <div><span>Familiar</span><strong>No extra controller. Just a phone browser.</strong></div>
      </div>
    </section>

    <section className={styles.setupSection} id="setup" aria-labelledby="setup-title">
      <div className={styles.setupHeading}>
        <p className={styles.sectionLabel}>Use what you already own</p>
        <h2 id="setup-title">One game. Three kinds of screen.</h2>
      </div>
      <div className={styles.setupGrid}>
        <article>
          <span>01</span>
          <h3>Computer</h3>
          <p>Hosts the game and gives the DM or dealer the controls.</p>
        </article>
        <article>
          <span>02</span>
          <h3>Shared display</h3>
          <p>A projector, flat screen, or TV becomes the surface everyone can see.</p>
        </article>
        <article>
          <span>03</span>
          <h3>Player phones</h3>
          <p>Hold private information and send each player&rsquo;s choices to the table.</p>
        </article>
      </div>
    </section>

    <section className={styles.closing} aria-labelledby="closing-title">
      <div>
        <p className={styles.sectionLabel}>Tonight&rsquo;s game can start now</p>
        <h2 id="closing-title">Bring the group.<br />TBLTOP will fill the gaps.</h2>
      </div>
      <div className={styles.closingActions}>
        <button type="button" className={styles.primaryAction} onClick={openTbltop}>Open TBLTOP</button>
        <button type="button" className={styles.textAction} onClick={onOpenMapCreator}>Build a map <span aria-hidden="true">→</span></button>
      </div>
    </section>

    <footer className={styles.footer}>
      <div className={styles.brand}>
        <span className={styles.brandMark}>TT</span>
        <span><strong>TBLTOP</strong><small>Tabletop Director</small></span>
      </div>
      <p>For projectors, tabletop screens, TVs, and the phones already at the table.</p>
      <div className={styles.footerLinks}>
        <a href="https://dnai.ca/en/privacy-policy" target="_blank" rel="noreferrer">Privacy</a>
        <button type="button" onClick={openCookiePreferences}>Cookie choices</button>
        <button type="button" onClick={onOpenPlayerRemote}>Player remote</button>
      </div>
    </footer>
    <CookieConsent />
  </main>;
}
